import * as React from 'react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { SimpleDrawer } from '@/components/ui/simple-drawer';
import { useIsSmallScreen } from '@/lib/hooks/use-media-query';
import { cn } from '@/utils';

import { getDayAbbreviation, getFullDayName } from '../../utils';
import { DayCheckbox } from '../day-checkbox';

interface BatchDayFilterProps {
  onApply?: (selectedDays: string[]) => void;
  onClear?: () => void;
  className?: string;
  defaultSelectedDays?: string[];
  dayCombinations?: Record<string, string[]>;
}

const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const BatchDayFilter: React.FC<BatchDayFilterProps> = ({
  onApply,
  onClear,
  className,
  defaultSelectedDays = [],
  dayCombinations = {},
}) => {
  // Convert abbreviated day names to full day names for internal state
  const initialSelectedDays = defaultSelectedDays.map(day =>
    getFullDayName(day)
  );
  const [selectedDays, setSelectedDays] =
    useState<string[]>(initialSelectedDays);

  const handleDaySelect = (day: string, position: number) => {
    setSelectedDays(prev => {
      const newSelection = [...prev];
      // If clicking the same day, unselect it (set to empty string)
      // If clicking a different day, select it
      newSelection[position] = newSelection[position] === day ? '' : day;
      return newSelection;
    });
  };

  const handleClear = () => {
    setSelectedDays([]);
    onClear?.();
  };

  const handleApply = () => {
    // Filter out empty strings and convert full day names to abbreviations for filtering logic
    const validDays = selectedDays.filter(Boolean);
    const abbreviatedDays = validDays.map(day => getDayAbbreviation(day));
    onApply?.(abbreviatedDays);
  };

  const [firstDay, secondDay] = selectedDays;

  // Get available days (days that exist in at least one batch)
  const availableDays = new Set(
    Object.keys(dayCombinations).map(abbrev => getFullDayName(abbrev))
  );

  // Helper function to check if a day should be enabled
  const isDayEnabled = (day: string, position: number): boolean => {
    const dayAbbrev = getDayAbbreviation(day);

    // If no day combinations data, enable all days except same day in other column
    if (Object.keys(dayCombinations).length === 0) {
      const otherSelectedDay = position === 0 ? secondDay : firstDay;
      return day !== otherSelectedDay;
    }

    // If day doesn't exist in any batch, disable it
    if (!dayCombinations[dayAbbrev]) return false;

    // If no day is selected in either column, enable only available days
    if (!firstDay && !secondDay) return availableDays.has(day);

    // If this is the selected day in this column, keep it enabled
    const currentSelectedDay = position === 0 ? firstDay : secondDay;
    if (day === currentSelectedDay) return true;

    // Get the other selected day
    const otherSelectedDay = position === 0 ? secondDay : firstDay;

    // Don't allow selecting the same day in both columns
    if (day === otherSelectedDay) return false;

    if (!otherSelectedDay) return availableDays.has(day);

    // Check if this day can be combined with the other selected day
    const otherDayAbbrev = getDayAbbreviation(otherSelectedDay);
    return dayCombinations[dayAbbrev]?.includes(otherDayAbbrev) || false;
  };

  return (
    <div
      className={cn(
        'bg-white rounded-2xl border border-gray-200 p-6 w-full shadow-sm',
        className
      )}
    >
      <h2 className="text-sm font-semibold mb-6 text-magical-moonlight-d3">
        Choose 2 days that work best for you
      </h2>

      <div className="grid grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-sm font-medium text-magical-moonlight-d1 mb-4">
            Select 1st Day
          </h3>
          <div className="space-y-3">
            {DAYS.map(day => (
              <DayCheckbox
                key={`first-${day}`}
                day={day}
                isSelected={day === firstDay}
                isDisabled={!isDayEnabled(day, 0)}
                onSelect={day => handleDaySelect(day, 0)}
                keyPrefix="first"
              />
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-magical-moonlight-d1 mb-4">
            Select 2nd Day
          </h3>
          <div className="space-y-3">
            {DAYS.map(day => (
              <DayCheckbox
                key={`second-${day}`}
                day={day}
                isSelected={day === secondDay}
                isDisabled={!isDayEnabled(day, 1)}
                onSelect={day => handleDaySelect(day, 1)}
                keyPrefix="second"
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={handleClear} className="flex-1">
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={selectedDays.filter(Boolean).length !== 2}
          className="flex-1"
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

export interface BatchDayFilterTriggerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  batchDayFilter: React.ReactNode;
  align?: 'start' | 'end';
  side?: 'top' | 'bottom';
  onApply?: (selectedDays: string[]) => void;
  onClear?: () => void;
  selectedDays?: string[];
  dayCombinations?: Record<string, string[]>;
}

const BatchDayFilterTrigger = React.forwardRef<
  HTMLDivElement,
  BatchDayFilterTriggerProps
>(
  (
    {
      className,
      children,
      batchDayFilter,
      align = 'start',
      side = 'bottom',
      onApply,
      onClear,
      selectedDays = [],
      dayCombinations = {},
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const filterRef = React.useRef<HTMLDivElement>(null);
    const isMobile = useIsSmallScreen();

    // Handle click outside and escape key
    React.useEffect(() => {
      if (!isOpen) return () => {};

      const handleClickOutside = (event: MouseEvent) => {
        if (
          triggerRef.current &&
          filterRef.current &&
          !triggerRef.current.contains(event.target as Node) &&
          !filterRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') setIsOpen(false);
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen]);

    const getPositionClasses = () =>
      `absolute z-50 ${
        side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
      } ${align === 'end' ? 'right-0' : 'left-0'}`;

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    const handleApply = (selectedDays: string[]) => {
      onApply?.(selectedDays);
      setIsOpen(false);
    };

    const handleClear = () => {
      onClear?.();
      setIsOpen(false);
    };

    const enhancedBatchDayFilter = React.isValidElement(batchDayFilter)
      ? React.cloneElement(batchDayFilter as React.ReactElement<any>, {
          onApply: handleApply,
          onClear: handleClear,
          defaultSelectedDays: selectedDays,
          dayCombinations,
          className: isMobile
            ? 'border-0 shadow-none rounded-none p-4'
            : undefined,
        })
      : batchDayFilter;

    const TriggerElement = (
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {children}
      </div>
    );

    return (
      <div
        ref={ref}
        className={cn('relative inline-block', className)}
        {...props}
      >
        {TriggerElement}
        {isMobile ? (
          <SimpleDrawer
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title="Choose your preferred days"
            size="fullWidth"
          >
            {enhancedBatchDayFilter}
          </SimpleDrawer>
        ) : (
          isOpen && (
            <div ref={filterRef} className={cn(getPositionClasses(), 'w-96')}>
              {enhancedBatchDayFilter}
            </div>
          )
        )}
      </div>
    );
  }
);

BatchDayFilterTrigger.displayName = 'BatchDayFilterTrigger';

export { BatchDayFilter, BatchDayFilterTrigger };
